/**
 * Goals Edit Page — hardcoded form.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne, useUpdate } from '@refinedev/core';
import {
  Button, Card, CardContent, Input, Label, Textarea,
} from '@yesbos/ui';
import { ArrowLeft } from 'lucide-react';

const STATUS_OPTIONS = ['not_started', 'in_progress', 'completed', 'on_hold', 'cancelled'];

export function GoalsEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading: fetchLoading } = useOne({ resource: 'goals', id: id! });
  const { mutate: update, isLoading: updateLoading } = useUpdate();
  const [form, setForm] = React.useState<Record<string, unknown> | null>(null);

  React.useEffect(() => { if (data?.data) setForm({ ...data.data }); }, [data]);
  const set = (f: string, v: unknown) => setForm(p => p ? { ...p, [f]: v } : p);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    const { id: _id, org_id, date_created, date_updated, employee_id, ...values } = form;
    update({ resource: 'goals', id: id!, values }, { onSuccess: () => navigate(`/goals/${id}`) });
  }

  if (fetchLoading || !form) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/goals/${id}`)}><ArrowLeft className="h-4 w-4" /></Button>
        <h1 className="text-2xl font-semibold">Edit Goal</h1>
      </div>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="title">Title *</Label>
              <Input id="title" value={String(form.title ?? '')} onChange={e => set('title', e.target.value)} required />
            </div>
            <div className="space-y-1">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" value={String(form.description ?? '')} onChange={e => set('description', e.target.value)} rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="start_date">Start Date</Label>
                <Input id="start_date" type="date" value={form.start_date ? String(form.start_date).slice(0, 10) : ''} onChange={e => set('start_date', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="due_date">Due Date</Label>
                <Input id="due_date" type="date" value={form.due_date ? String(form.due_date).slice(0, 10) : ''} onChange={e => set('due_date', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm"
                  value={String(form.status ?? 'not_started')}
                  onChange={e => set('status', e.target.value)}
                >
                  {STATUS_OPTIONS.map(s => (
                    <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="progress">Progress (%)</Label>
                <Input id="progress" type="number" min={0} max={100} value={Number(form.progress ?? 0)} onChange={e => set('progress', parseInt(e.target.value) || 0)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="weight">Weight</Label>
              <Input id="weight" type="number" min={0} max={100} value={Number(form.weight ?? 100)} onChange={e => set('weight', parseInt(e.target.value) || 100)} />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate(`/goals/${id}`)}>Cancel</Button>
              <Button type="submit" disabled={updateLoading}>{updateLoading ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default GoalsEditPage;
